import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useLoginWithAzure } from '../hooks/useLoginWithAzure';
import { consumeRedirectAfterLogin } from '../utils/redirectAfterLogin';
import { LoginForm } from './LoginForm';
import { DevLoginForm } from './DevLoginForm';
import { Button } from '@ntconsult/aluma-react';

const localAuthEnabled =
  (import.meta.env.VITE_LOCAL_AUTH_ENABLED ?? 'true') !== 'false';

const devImpersonationEnabled =
  import.meta.env.DEV &&
  (import.meta.env.VITE_DEV_IMPERSONATION_ENABLED ?? 'false') === 'true';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const { isAuthenticated } = useAuth();
  const loginWithAzure = useLoginWithAzure();

  if (isAuthenticated) {
    return <Navigate to={consumeRedirectAfterLogin()} replace />;
  }

  return (
    <div
      className="min-h-[100dvh] flex items-center justify-center bg-surface-raised px-4"
      style={{
        paddingTop: 'max(env(safe-area-inset-top), 1rem)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 1rem)',
        paddingLeft: 'max(env(safe-area-inset-left), 1rem)',
        paddingRight: 'max(env(safe-area-inset-right), 1rem)',
      }}
    >
      <div className="w-full max-w-md">
        <div className="bg-surface rounded-2xl shadow-xl p-6 sm:p-8 border border-border">
          <div className="text-center mb-8">
            <img src="/icone_logo_nt.svg" alt="NT Files" className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground">NT Files</h1>
            <p className="text-foreground-muted mt-1">{t('title')}</p>
          </div>

          {localAuthEnabled && (
            <>
              <LoginForm />

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-surface text-foreground-muted">{t('or')}</span>
                </div>
              </div>
            </>
          )}

          <Button variant="secondary" className="w-full" onClick={loginWithAzure}>
            <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" aria-hidden="true">
              <path fill="#f25022" d="M1 1h9v9H1z" />
              <path fill="#00a4ef" d="M1 11h9v9H1z" />
              <path fill="#7fba00" d="M11 1h9v9h-9z" />
              <path fill="#ffb900" d="M11 11h9v9h-9z" />
            </svg>
            {t('loginWithMicrosoft')}
          </Button>

          {devImpersonationEnabled && <DevLoginForm />}

          <p className="text-center text-xs text-foreground-subtle mt-6">
            {t('subtitle')}
          </p>
        </div>
      </div>
    </div>
  );
}
